/**
 * useSearch Hook
 *
 * Manages search state, filters, and API calls for the instructor directory
 */

import { useState, useEffect, useRef } from 'preact/hooks';
import {
  searchInstructors,
  type SearchFilters,
  type SearchResults,
  type InstructorCard
} from '../services/instructorService';

interface UseSearchReturn {
  // Data
  instructors: InstructorCard[];
  total: number;
  page: number;
  perPage: number;
  totalPages: number;
  hasMore: boolean;

  // Filters
  filters: SearchFilters;

  // State
  loading: boolean;
  error: string | null;

  // Actions
  updateFilter: (key: keyof SearchFilters, value: any) => void;
  clearFilters: () => void;
  setPage: (page: number) => void;
  setPerPage: (perPage: number) => void;
  refresh: () => void;
}

/**
 * Custom hook for managing instructor search
 *
 * @param initialFilters Optional initial filter values
 * @returns Search state and control functions
 */
export function useSearch(initialFilters: SearchFilters = {}): UseSearchReturn {
  // State
  const [instructors, setInstructors] = useState<InstructorCard[]>([]);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    perPage: 20,
    totalPages: 0,
    hasMore: false
  });
  const [filters, setFilters] = useState<SearchFilters>(initialFilters);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Ref to track if this is the first render
  const isFirstRender = useRef(true);

  // Debounce timeout ref
  const searchTimeoutRef = useRef<number | null>(null);

  /**
   * Perform search with current filters and pagination
   */
  const performSearch = async () => {
    setLoading(true);
    setError(null);

    try {
      const results: SearchResults = await searchInstructors(
        filters,
        pagination.page,
        pagination.perPage
      );

      setInstructors(results.instructors);
      setPagination(results.pagination);
    } catch (err: any) {
      console.error('[useSearch] Error searching instructors:', err);
      setError(err.message || 'Error al buscar instructores');
      setInstructors([]);
      setPagination({
        total: 0,
        page: 1,
        perPage: pagination.perPage,
        totalPages: 0,
        hasMore: false
      });
    } finally {
      setLoading(false);
    }
  };

  /**
   * Update a single filter
   */
  const updateFilter = (key: keyof SearchFilters, value: any) => {
    setFilters((prev) => {
      const newFilters: SearchFilters = { ...prev };

      // Remove filter if value is empty/null/undefined
      if (value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0)) {
        delete newFilters[key];
      } else {
        (newFilters as any)[key] = value;
      }

      return newFilters;
    });

    // Reset to page 1 when filters change
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  /**
   * Clear all filters
   */
  const clearFilters = () => {
    setFilters({});
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  /**
   * Set current page
   */
  const setPage = (page: number) => {
    setPagination((prev) => ({ ...prev, page }));
  };

  /**
   * Set results per page
   */
  const setPerPage = (perPage: number) => {
    setPagination((prev) => ({ ...prev, perPage, page: 1 }));
  };

  /**
   * Manually refresh search results
   */
  const refresh = () => {
    performSearch();
  };

  /**
   * Effect: Perform search when filters or pagination change
   * Uses debounce to avoid excessive API calls (except on first render)
   */
  useEffect(() => {
    // On first render, perform search immediately without debounce
    if (isFirstRender.current) {
      isFirstRender.current = false;
      performSearch();
      return;
    }

    // Clear any existing timeout
    if (searchTimeoutRef.current !== null) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set debounced search for subsequent changes
    // Shorter debounce for pagination changes (100ms), longer for filter changes (500ms)
    const debounceMs = Object.keys(filters).length > 0 ? 500 : 100;

    searchTimeoutRef.current = window.setTimeout(() => {
      performSearch();
    }, debounceMs);

    // Cleanup
    return () => {
      if (searchTimeoutRef.current !== null) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [filters, pagination.page, pagination.perPage]);

  return {
    // Data
    instructors,
    total: pagination.total,
    page: pagination.page,
    perPage: pagination.perPage,
    totalPages: pagination.totalPages,
    hasMore: pagination.hasMore,

    // Filters
    filters,

    // State
    loading,
    error,

    // Actions
    updateFilter,
    clearFilters,
    setPage,
    setPerPage,
    refresh
  };
}
